// HTTP Service with enhanced error handling
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { API_BASE_URL } from "../constants";
import { ApiResponse, normalizeApiResponse } from "../types/api";
import { isJwtExpired } from "../utils/jwt";
import getValidFirebaseToken from "../utils/valid_firebase_token";

// Extend AxiosRequestConfig to include metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

// API Error response interface
interface ApiErrorResponse {
  message?: string;
  error?: string;
  details?: any;
}

// Custom exception classes
export class ApiException extends Error {
  constructor(message: string, public statusCode?: number, public data?: any) {
    super(message);
    this.name = "ApiException";
  }
}

export class ValidationException extends ApiException {
  constructor(message: string, data?: any) {
    super(message, 422, data);
    this.name = "ValidationException";
  }
}

export class AuthenticationException extends ApiException {
  constructor(message: string = "Authentication failed") {
    super(message, 401);
    this.name = "AuthenticationException";
  }
}

export class NetworkException extends ApiException {
  constructor(message: string = "Network error") {
    super(message, 0);
    this.name = "NetworkException";
  }
}

class HttpService {
  private axiosInstance;

  constructor() {
    if (!API_BASE_URL) {
      throw new Error(
        "API_BASE_URL is not defined! Check your .env or .env.production file and restart the app."
      );
    }
    console.log("[httpService] API_BASE_URL:", API_BASE_URL);
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async (config: ExtendedAxiosRequestConfig) => {
        try {
          let token = localStorage.getItem("authToken");

          if (token && isJwtExpired(token)) {
            console.log("Token expired, refreshing...");
            token = await getValidFirebaseToken();
          }

          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }

          // Add request timestamp for debugging
          config.metadata = { startTime: Date.now() };

          console.log(
            `🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`
          );

          return config;
        } catch (error) {
          console.error("Request interceptor error:", error);
          return Promise.reject(
            new AuthenticationException("Failed to authenticate request")
          );
        }
      },
      (error) => {
        return Promise.reject(
          new NetworkException("Failed to prepare request")
        );
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const config = response.config as ExtendedAxiosRequestConfig;
        const requestTime = Date.now() - (config.metadata?.startTime || 0);
        console.log(`✅ API Response: ${response.status} (${requestTime}ms)`);

        return response;
      },
      (error: AxiosError) => {
        this.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  private handleError(error: AxiosError) {
    const response = error.response;
    const config = error.config as ExtendedAxiosRequestConfig;
    const requestTime = Date.now() - (config?.metadata?.startTime || 0);

    console.error(
      `❌ API Error: ${response?.status || "Network"} (${requestTime}ms)`,
      {
        url: error.config?.url,
        method: error.config?.method,
        status: response?.status,
        data: response?.data,
      }
    );

    if (!response) {
      throw new NetworkException(
        "Network error - please check your connection"
      );
    }

    switch (response.status) {
      case 400:
        throw new ApiException("Bad request", 400, response.data);

      case 401:
        localStorage.removeItem("authToken");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        throw new AuthenticationException("Authentication required");

      case 403:
        throw new ApiException("Access forbidden", 403, response.data);

      case 404:
        throw new ApiException("Resource not found", 404, response.data);

      case 422: // YENİ: Validation error status kodu
        const errorData = response.data as ApiErrorResponse;
        const message =
          errorData?.message || errorData?.error || "Validation error";
        throw new ValidationException(message, response.data);

      case 429:
        throw new ApiException("Too many requests", 429, response.data);

      case 500:
        throw new ApiException("Internal server error", 500, response.data);

      case 502:
      case 503:
      case 504:
        throw new ApiException(
          "Service unavailable",
          response.status,
          response.data
        );

      default:
        throw new ApiException(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          response.data
        );
    }
  }

  // Generic HTTP methods with response normalization
  async get<T>(url: string, config?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get(url, config);
    return normalizeApiResponse(response.data);
  }

  async post<T>(
    url: string,
    data?: any,
    config?: any
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.post(url, data, config);
    return normalizeApiResponse(response.data);
  }

  async put<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.put(url, data, config);
    return normalizeApiResponse(response.data);
  }

  async delete<T>(url: string, config?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete(url, config);
    return normalizeApiResponse(response.data);
  }

  // Raw methods for backward compatibility
  async getRaw(url: string, config?: any) {
    return this.axiosInstance.get(url, config);
  }

  async postRaw(url: string, data?: any, config?: any) {
    return this.axiosInstance.post(url, data, config);
  }

  async putRaw(url: string, data?: any, config?: any) {
    return this.axiosInstance.put(url, data, config);
  }

  async deleteRaw(url: string, config?: any) {
    return this.axiosInstance.delete(url, config);
  }
}

export default new HttpService();
